import { InvalidEmailCell } from "@ext/enterprise/components/admin/settings/components/InvalidEmailCell";
import { emailKey, type MemberAggregate } from "@ext/enterprise/components/admin/settings/members/model/Member";
import t from "@ext/localization/locale/translate";
import { Checkbox } from "@ui-kit/Checkbox";
import type { ColumnDef } from "@ui-kit/DataTable";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";
import { editColumn } from "../../../../../../enterprise/components/admin/ui-kit/table/columns";
import { TABLE_SELECT_COLUMN_CODE } from "../../../../../../enterprise/components/admin/ui-kit/table/TableComponent";
import { GesCommonUserBadge } from "../../../../../../enterpriseCommon/components/roleBadges/GesCommonUserBadge";
import {
	onwerBadgeIcon,
	userBadgesColumnId,
} from "../../../../../../enterpriseCommon/components/roleBadges/UserBadgesConfig";
import { UserBadgesWrapper } from "../../../../../../enterpriseCommon/components/roleBadges/UserBadgesWrapper";
import type { GesCloudMember } from "../types/GesCloudUsersComponentTypes";

interface GesCloudUsersTableColumnsArgs {
	isCurrentUserOwner: boolean;
	canEditMembers: boolean;
	aggregate: MemberAggregate;
}

export const getGesCloudUsersTableColumns = ({
	isCurrentUserOwner,
	canEditMembers,
	aggregate,
}: GesCloudUsersTableColumnsArgs): ColumnDef<GesCloudMember>[] => {
	return [
		{
			id: TABLE_SELECT_COLUMN_CODE,
			size: 32,
			header: ({ table }) => {
				const changeableRows = table
					.getRowModel()
					.rows.filter(
						(row) =>
							row.original.type === "invite" ||
							(!row.original.isOwner && !(row.original.isAdmin && !isCurrentUserOwner)),
					);

				const allChangeableSelected =
					changeableRows.length && changeableRows.every((row) => row.getIsSelected());
				const someChangeableSelected = changeableRows.some((row) => row.getIsSelected());

				const headerCheckedState = allChangeableSelected
					? true
					: someChangeableSelected
						? "indeterminate"
						: false;

				return (
					<Checkbox
						aria-label="Select all"
						checked={headerCheckedState}
						onCheckedChange={(value) =>
							changeableRows.forEach((row) => {
								row.toggleSelected(!!value);
							})
						}
					/>
				);
			},
			cell: ({ row }) => (
				<Checkbox
					aria-label="Select row"
					checked={row.getIsSelected()}
					disabled={
						row.original.type === "user" &&
						(row.original.isOwner || (row.original.isAdmin && !isCurrentUserOwner))
					}
					onCheckedChange={(value) => row.toggleSelected(!!value)}
				/>
			),
			enableSorting: false,
			enableHiding: false,
		},
		...(canEditMembers ? [editColumn<GesCloudMember>()] : []),
		{
			accessorKey: "email",
			header: t("enterprise-cloud.org-settings.users.email"),
			cell: ({ row }) => <InvalidEmailCell value={row.original.email} />,
		},
		{
			accessorKey: "name",
			header: t("enterprise-cloud.org-settings.users.name"),
			cell: ({ row }) => <TextOverflowTooltip>{row.original.name ?? ""}</TextOverflowTooltip>,
		},
		{
			accessorKey: "type",
			header: t("enterprise-cloud.org-settings.users.status-header"),
			cell: ({ row }) => {
				const type = row.original.type;
				return (
					<span>
						{type === "user"
							? t("enterprise-cloud.org-settings.users.status.user")
							: t("enterprise-cloud.org-settings.users.status.invite")}
					</span>
				);
			},
		},
		{
			id: userBadgesColumnId,
			cell: ({ row }) => {
				const item = row.original;
				const isEditor = Boolean(aggregate.usersByValue.get(emailKey(item.email))?.isEditor);

				if (!item.isAdmin && !item.isOwner && !isEditor) return null;

				return (
					<UserBadgesWrapper>
						{(item.isAdmin || item.isOwner) && (
							<GesCommonUserBadge
								icon={item.isOwner ? onwerBadgeIcon : ""}
								label={
									item.isOwner
										? t("enterprise-cloud.org-settings.users.roles.owner")
										: t("enterprise-cloud.org-settings.users.roles.admin")
								}
							/>
						)}
						{isEditor && <GesCommonUserBadge label={t("enterprise.admin.users.editor")} />}
					</UserBadgesWrapper>
				);
			},
		},
	];
};
